import * as z from "zod";
import { analyticsScalarFunctionAliases } from "../query-plan/custom-analytics-query-function-signatures";
import {
  CUSTOM_ANALYTICS_QUERY_ROW_LIMIT_MAX,
  CUSTOM_ANALYTICS_QUERY_SORT_LIMIT,
} from "../shared/custom-analytics-query-limits";
import {
  CustomAnalyticsQueryDatePartSchema,
  CustomAnalyticsQueryScalarValueSchema,
  CustomAnalyticsQuerySortDirectionSchema,
  CustomAnalyticsQueryTimeUnitSchema,
} from "../shared/custom-analytics-query-primitives";
import {
  ANALYTICS_QUERY_MAX_RELATIVE_FILTERS,
  ANALYTICS_RELATIVE_MAX_ARGS,
  ANALYTICS_RELATIVE_MAX_SORT_TERMS,
  ANALYTICS_RELATIVE_MAX_WITHIN_TERMS,
  AnalyticsAggregateNullHandling,
  AnalyticsAggregateOp,
  AnalyticsAttributeColumnSource,
  AnalyticsColumnType,
  AnalyticsConditionNodeType,
  AnalyticsFormulaBinaryOperation,
  AnalyticsFormulaNodeType,
  AnalyticsGrain,
  AnalyticsMetricColumnSource,
  AnalyticsRelativeBound,
  AnalyticsRelativeFrameMode,
  AnalyticsRelativeNullOrdering,
  AnalyticsRelativeNullTreatment,
  AnalyticsRelativeOnlyOperation,
  AnalyticsRelativeTimeAnchor,
  AnalyticsScalarCastType,
  AnalyticsScalarFunctionName,
  AnalyticsScalarNodeType,
  AnalyticsTimeBoundaryType,
  CUSTOM_ANALYTICS_SEMANTIC_QUERY_VERSION,
} from "./analytics-query-constants";
import {
  AnalyticsComparisonOpsWithoutValue,
  AnalyticsComparisonOpsWithValue,
} from "./analytics-query-filter-ops";
import {
  AnalyticsColumnOutputRefSchema,
  AnalyticsDimensionOutputIdSchema,
  AnalyticsDimensionOutputRefSchema,
  AnalyticsMetricOutputIdSchema,
  AnalyticsMetricOutputRefSchema,
  AnalyticsRefSyntaxSchema,
  AnalyticsRoutedRefSchema,
  type AnalyticsExpressionRef,
} from "./analytics-query-refs";
import type {
  AnalyticsCatalogFormulaExpression,
  AnalyticsColumn,
  AnalyticsConditionExpression,
  AnalyticsRelativeFilter,
  AnalyticsRelativeFrame,
  AnalyticsRelativeFrameBound,
  AnalyticsRelativeSpec,
  AnalyticsScalarExpression,
  AnalyticsTimeBoundary,
  CustomAnalyticsSemanticQuery,
} from "./analytics-query-types";

export const AnalyticsLabelSchema = z.string().min(1).max(120);

const AnalyticsComparisonRefSchema = z.union([
  AnalyticsRoutedRefSchema,
  AnalyticsMetricOutputRefSchema,
]);

const AnalyticsAbsoluteDateBoundarySchema = z.strictObject({
  type: z.literal(AnalyticsTimeBoundaryType.AbsoluteDate),
  value: z.iso.date(),
}) satisfies z.ZodType<
  Extract<
    AnalyticsTimeBoundary,
    { readonly type: typeof AnalyticsTimeBoundaryType.AbsoluteDate }
  >
>;

const AnalyticsCurrentDateBoundarySchema = z.strictObject({
  type: z.literal(AnalyticsTimeBoundaryType.CurrentDate),
}) satisfies z.ZodType<
  Extract<
    AnalyticsTimeBoundary,
    { readonly type: typeof AnalyticsTimeBoundaryType.CurrentDate }
  >
>;

const AnalyticsRelativeDateBoundarySchema = z.strictObject({
  type: z.literal(AnalyticsTimeBoundaryType.RelativeDate),
  grain: CustomAnalyticsQueryDatePartSchema,
  amount: z.int().min(-10_000).max(10_000),
  anchor: z.enum(AnalyticsRelativeTimeAnchor).optional(),
}) satisfies z.ZodType<
  Extract<
    AnalyticsTimeBoundary,
    { readonly type: typeof AnalyticsTimeBoundaryType.RelativeDate }
  >
>;

const AnalyticsTimeBoundarySchema = z.discriminatedUnion("type", [
  AnalyticsAbsoluteDateBoundarySchema,
  AnalyticsCurrentDateBoundarySchema,
  AnalyticsRelativeDateBoundarySchema,
]) satisfies z.ZodType<AnalyticsTimeBoundary>;

export const AnalyticsFilterValueSchema = z.union([
  CustomAnalyticsQueryScalarValueSchema,
  AnalyticsTimeBoundarySchema,
  z.array(CustomAnalyticsQueryScalarValueSchema).min(1).max(100),
]);

function analyticsConditionExpressionSchema<T extends AnalyticsExpressionRef>(
  onRefSchema: z.ZodType<T>,
): z.ZodType<AnalyticsConditionExpression<T>> {
  const schema: z.ZodType<AnalyticsConditionExpression<T>> = z.lazy(() =>
    z.union([
      z.discriminatedUnion("operation", [
        z.strictObject({
          type: z.literal(AnalyticsConditionNodeType.Comparison),
          operation: z.enum(AnalyticsComparisonOpsWithValue),
          on: onRefSchema,
          value: AnalyticsFilterValueSchema,
        }),
        z.strictObject({
          type: z.literal(AnalyticsConditionNodeType.Comparison),
          operation: z.enum(AnalyticsComparisonOpsWithoutValue),
          on: onRefSchema,
        }),
      ]),
      z.strictObject({
        type: z.enum([
          AnalyticsConditionNodeType.And,
          AnalyticsConditionNodeType.Or,
        ]),
        conditions: z.array(schema).min(2).max(20),
      }),
      z.strictObject({
        type: z.literal(AnalyticsConditionNodeType.Not),
        condition: schema,
      }),
    ]),
  );
  return schema;
}

export const AnalyticsConditionExpressionSchema =
  analyticsConditionExpressionSchema(AnalyticsComparisonRefSchema);

const ANALYTICS_SCALAR_FUNCTION_ALIASES = analyticsScalarFunctionAliases();

const ANALYTICS_SCALAR_REMOVED_FUNCTION_GUIDANCE: Readonly<
  Record<string, string>
> = {
  if: 'Analytics scalar function "if" was removed: use a case expression with an explicit comparison predicate.',
};

const AnalyticsScalarFunctionNameSchema = z.preprocess(
  (value) =>
    typeof value === "string"
      ? (ANALYTICS_SCALAR_FUNCTION_ALIASES[value] ?? value)
      : value,
  z.enum(AnalyticsScalarFunctionName, {
    error: (issue) =>
      ANALYTICS_SCALAR_REMOVED_FUNCTION_GUIDANCE[String(issue.input)] ??
      `Unknown analytics scalar function "${String(issue.input)}".`,
  }),
);

export const AnalyticsFormulaExpressionSchema: z.ZodType<AnalyticsCatalogFormulaExpression> =
  z.lazy(() =>
    z.discriminatedUnion("type", [
      z.strictObject({
        type: z.literal(AnalyticsFormulaNodeType.Aggregate),
        operation: z.enum(AnalyticsAggregateOp),
        on: AnalyticsRefSyntaxSchema,
        nullHandling: z.enum(AnalyticsAggregateNullHandling).optional(),
        quantile: z.number().gt(0).lt(1).optional(),
      }),
      z.strictObject({
        type: z.literal(AnalyticsFormulaNodeType.MetricRef),
        ref: AnalyticsRefSyntaxSchema,
      }),
      z.strictObject({
        type: z.literal(AnalyticsFormulaNodeType.Binary),
        operation: z.enum(AnalyticsFormulaBinaryOperation),
        left: AnalyticsFormulaExpressionSchema,
        right: AnalyticsFormulaExpressionSchema,
      }),
    ]),
  );

export const AnalyticsRelativeOperationSchema = z.enum([
  ...Object.values(AnalyticsAggregateOp),
  ...Object.values(AnalyticsRelativeOnlyOperation),
]);

const AnalyticsRelativeFrameBoundSchema: z.ZodType<AnalyticsRelativeFrameBound> =
  z
    .strictObject({
      bound: z.enum(AnalyticsRelativeBound),
      offset: z.number().optional(),
      unit: CustomAnalyticsQueryTimeUnitSchema.optional(),
    })
    .refine(
      (bound) =>
        (bound.offset !== undefined) ===
        (bound.bound === AnalyticsRelativeBound.Preceding ||
          bound.bound === AnalyticsRelativeBound.Following),
      {
        message: `A relative frame bound carries an offset exactly when it is ${AnalyticsRelativeBound.Preceding} or ${AnalyticsRelativeBound.Following}.`,
      },
    )
    .refine((bound) => bound.unit === undefined || bound.offset !== undefined, {
      message:
        "A relative frame bound with no offset cannot carry a time unit.",
    });

const AnalyticsRelativeFrameSchema: z.ZodType<AnalyticsRelativeFrame> =
  z.strictObject({
    mode: z.enum(AnalyticsRelativeFrameMode),
    start: AnalyticsRelativeFrameBoundSchema,
    end: AnalyticsRelativeFrameBoundSchema,
  });

export function analyticsRelativeSpecSchema<T extends AnalyticsExpressionRef>(
  refSchema: z.ZodType<T>,
): z.ZodType<AnalyticsRelativeSpec<T>> {
  const termSchema = analyticsScalarExpressionSchema(refSchema);
  return z.strictObject({
    within: z
      .array(termSchema)
      .min(1)
      .max(ANALYTICS_RELATIVE_MAX_WITHIN_TERMS)
      .optional(),
    sorts: z
      .array(
        z.strictObject({
          on: termSchema,
          direction: CustomAnalyticsQuerySortDirectionSchema.default("asc"),
          nulls: z.enum(AnalyticsRelativeNullOrdering),
        }),
      )
      .min(1)
      .max(ANALYTICS_RELATIVE_MAX_SORT_TERMS)
      .optional(),
    frame: AnalyticsRelativeFrameSchema.optional(),
  });
}

export interface AnalyticsScalarExpressionOptions {
  readonly allowRelative?: boolean;
}

export function analyticsScalarExpressionSchema<
  T extends AnalyticsExpressionRef,
>(
  refSchema: z.ZodType<T>,
  options?: AnalyticsScalarExpressionOptions,
): z.ZodType<AnalyticsScalarExpression<T>> {
  const predicateSchema = analyticsConditionExpressionSchema(refSchema);
  const relativeArms = options?.allowRelative
    ? [
        z.strictObject({
          type: z.literal(AnalyticsScalarNodeType.Relative),
          operation: AnalyticsRelativeOperationSchema,
          args: z
            .array(analyticsScalarExpressionSchema(refSchema))
            .max(ANALYTICS_RELATIVE_MAX_ARGS),
          quantile: z.number().gt(0).lt(1).optional(),
          nullTreatment: z.enum(AnalyticsRelativeNullTreatment).optional(),
          relativeTo: analyticsRelativeSpecSchema(refSchema),
        }),
      ]
    : [];
  const schema: z.ZodType<AnalyticsScalarExpression<T>> = z.lazy(() =>
    z.discriminatedUnion("type", [
      z.strictObject({
        type: z.literal(AnalyticsScalarNodeType.AttributeRef),
        ref: refSchema,
      }),
      z.strictObject({
        type: z.literal(AnalyticsScalarNodeType.Literal),
        value: CustomAnalyticsQueryScalarValueSchema,
      }),
      z.strictObject({
        type: z.literal(AnalyticsScalarNodeType.Function),
        fn: AnalyticsScalarFunctionNameSchema,
        unit: CustomAnalyticsQueryTimeUnitSchema.optional(),
        args: z.array(schema).min(0).max(8),
      }),
      z.strictObject({
        type: z.literal(AnalyticsScalarNodeType.Cast),
        expr: schema,
        dataType: z.enum(AnalyticsScalarCastType),
      }),
      z.strictObject({
        type: z.literal(AnalyticsScalarNodeType.Binary),
        op: z.enum(AnalyticsFormulaBinaryOperation),
        left: schema,
        right: schema,
      }),
      z.strictObject({
        type: z.literal(AnalyticsScalarNodeType.Case),
        whens: z
          .array(z.strictObject({ when: predicateSchema, then: schema }))
          .min(1)
          .max(16),
        else: schema,
      }),
      ...relativeArms,
    ]),
  );
  return schema;
}

export const AnalyticsScalarExpressionSchema = analyticsScalarExpressionSchema(
  AnalyticsRoutedRefSchema,
);

/**
 * Row-level expressions may hold a relative node; the compiler hoists it into
 * the windowed stage. A filter on one names the selected derived column by its
 * own id, or a catalog attribute of the base grain.
 */
export const AnalyticsRowExpressionSchema = analyticsScalarExpressionSchema(
  AnalyticsRoutedRefSchema,
  { allowRelative: true },
);

export const AnalyticsRelativeFilterSchema: z.ZodType<AnalyticsRelativeFilter> =
  analyticsConditionExpressionSchema(
    z.union([AnalyticsRoutedRefSchema, AnalyticsDimensionOutputRefSchema]),
  );

export const AnalyticsPostAggregateExpressionSchema =
  analyticsScalarExpressionSchema(AnalyticsColumnOutputRefSchema, {
    allowRelative: true,
  });

const AnalyticsMetricColumnBaseSchema = z.strictObject({
  type: z.literal(AnalyticsColumnType.Metric),
  id: AnalyticsMetricOutputIdSchema,
  label: AnalyticsLabelSchema.optional(),
});

export const AnalyticsColumnSchema: z.ZodType<AnalyticsColumn> = z.union([
  z.strictObject({
    type: z.literal(AnalyticsColumnType.Attribute),
    source: z.literal(AnalyticsAttributeColumnSource.Catalog).optional(),
    id: AnalyticsDimensionOutputIdSchema.optional(),
    on: AnalyticsRoutedRefSchema,
    bucket: z
      .strictObject({ grain: CustomAnalyticsQueryDatePartSchema })
      .optional(),
    label: AnalyticsLabelSchema.optional(),
  }),
  z.strictObject({
    type: z.literal(AnalyticsColumnType.Attribute),
    source: z.literal(AnalyticsAttributeColumnSource.Expression),
    id: AnalyticsDimensionOutputIdSchema,
    expression: AnalyticsRowExpressionSchema,
    label: AnalyticsLabelSchema.optional(),
  }),
  z.discriminatedUnion("source", [
    AnalyticsMetricColumnBaseSchema.extend({
      source: z.literal(AnalyticsMetricColumnSource.Catalog),
      ref: AnalyticsRoutedRefSchema,
      filterRefs: z.array(AnalyticsRefSyntaxSchema).min(1).max(10).optional(),
    }),
    AnalyticsMetricColumnBaseSchema.extend({
      source: z.literal(AnalyticsMetricColumnSource.Formula),
      formula: AnalyticsFormulaExpressionSchema,
    }),
    AnalyticsMetricColumnBaseSchema.extend({
      source: z.literal(AnalyticsMetricColumnSource.Expression),
      expression: AnalyticsPostAggregateExpressionSchema,
    }),
  ]),
]);

export const CustomAnalyticsSemanticQuerySchema: z.ZodType<CustomAnalyticsSemanticQuery> =
  z.strictObject({
    version: z
      .literal(CUSTOM_ANALYTICS_SEMANTIC_QUERY_VERSION)
      .default(CUSTOM_ANALYTICS_SEMANTIC_QUERY_VERSION),
    baseGrain: z.enum(AnalyticsGrain).optional(),
    columns: z.array(AnalyticsColumnSchema).min(1).max(45),
    filterRecords: z
      .array(AnalyticsConditionExpressionSchema)
      .max(20)
      .optional(),
    filterRelative: z
      .array(AnalyticsRelativeFilterSchema)
      .max(ANALYTICS_QUERY_MAX_RELATIVE_FILTERS)
      .optional(),
    filterGroups: z
      .array(AnalyticsConditionExpressionSchema)
      .max(20)
      .optional(),
    sorts: z
      .array(
        z.strictObject({
          on: z.union([
            AnalyticsRoutedRefSchema,
            AnalyticsMetricOutputRefSchema,
            AnalyticsDimensionOutputRefSchema,
          ]),
          direction: CustomAnalyticsQuerySortDirectionSchema.default("asc"),
        }),
      )
      .max(CUSTOM_ANALYTICS_QUERY_SORT_LIMIT)
      .optional(),
    limit: z
      .int()
      .min(1)
      .max(CUSTOM_ANALYTICS_QUERY_ROW_LIMIT_MAX)
      .nullable()
      .optional(),
  });

export function parseCustomAnalyticsSemanticQuery(
  query: unknown,
): CustomAnalyticsSemanticQuery {
  return CustomAnalyticsSemanticQuerySchema.parse(query);
}
